import React, { useCallback, useEffect, useMemo } from 'react';
import { logger } from '../../common/logger';
import { loadBraze } from './loadBraze';
import { brazeBaseUrl } from './constants';
import {
  ContentCard,
  ContextValue,
  InAppMessage,
} from '../../client/braze/types';
import { Language } from '@yleisradio/areena-types';
import { loginFetchClient as getLoginFetch } from '../../service/loginFetchClient';
import { BrazeContext } from './BrazeContext';
import { yleConsentSdkHeadless } from '@yleisradio/consent-sdk/headless';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { useFeatureFlag } from '../contexts/FeatureFlagContext/FeatureFlagContext';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { initializeBraze } from './initializeBraze';

type Props = {
  language: Language;
};

export const BrazeProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  language,
}) => {
  const { brazeEnabled } = useFeatureFlag();
  const { brazeApiKey } = useAppFeaturesContext();
  const { user } = useAuthContext();
  const isTargetingConsentGiven = yleConsentSdkHeadless.getConsent().targeting;

  const isBrazeAllowed =
    !!user && !user.isSubAccount && isTargetingConsentGiven && brazeEnabled;

  const [isBrazeLoaded, setIsBrazeLoaded] = React.useState(false);
  const [inAppMessages, setInAppMessages] = React.useState<InAppMessage[]>([]);
  const [contentCards, setContentCards] = React.useState<ContentCard[]>([]);

  const removeMessage = useCallback((msg: InAppMessage) => {
    setInAppMessages(messages =>
      messages.filter(message => message.triggerId !== msg.triggerId)
    );

    const braze = window.braze;
    if (!braze) {
      return;
    }
  }, []);

  useEffect(() => {
    if (!isBrazeAllowed || isBrazeLoaded) {
      return;
    }

    logger.debug('Loading Braze.');

    loadBraze(() => setIsBrazeLoaded(true));
  }, [isBrazeAllowed, isBrazeLoaded]);

  const loginFetch = useMemo(() => getLoginFetch(), []);

  useEffect(() => {
    const braze = window.braze;
    if (!isBrazeLoaded || !braze || !isBrazeAllowed) {
      return;
    }

    if (brazeApiKey === undefined) {
      logger.error('No Braze API key found. Cancelling Braze initialization.');
      return;
    }

    initializeBraze({
      apiKey: brazeApiKey,
      appendInAppMessage: message =>
        setInAppMessages(messages => messages.concat(message)),
      baseUrl: brazeBaseUrl,
      braze,
      language,
      loginFetch,
      setContentCards,
      userId: user.userId,
    })
      .then(() => logger.debug('Braze initialized'))
      .catch(e => logger.debug({ error: e }, 'Braze initialization failed'));

    return () => {
      logger.debug('Terminating Braze.');

      braze.wipeData();
      braze.destroy();
    };
  }, [brazeApiKey, isBrazeLoaded, isBrazeAllowed, language, user, loginFetch]);

  const inAppMessageClick = useCallback((message: InAppMessage) => {
    const braze = window.braze;
    if (!braze) {
      return;
    }
    braze.logInAppMessageClick(message);
  }, []);

  const contentCardLogImpression = useCallback((card: ContentCard) => {
    const braze = window.braze;
    if (!braze) return;

    braze.logContentCardImpressions([card]);
  }, []);

  const contentCardLogClick = useCallback((card: ContentCard) => {
    const braze = window.braze;
    if (!braze) return;

    braze.logContentCardClick(card);
  }, []);

  const value: ContextValue = useMemo(
    () => ({
      contentCards,
      contentCardLogClick,
      contentCardLogImpression,
      isBrazeInitialized: window.braze !== undefined,
      inAppMessages,
      inAppMessageClick,
      removeMessage,
    }),
    [
      contentCards,
      contentCardLogClick,
      contentCardLogImpression,
      inAppMessages,
      inAppMessageClick,
      removeMessage,
    ]
  );

  return (
    <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>
  );
};
